// PREZENCE — Employee List (Admin)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../api/client';
import toast from 'react-hot-toast';

const getInitials = n => (n||'').split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase();
const COLORS = ['#63dcbc','#3ba8ff','#a78bfa','#f59e0b','#22c55e','#ef4444'];

export default function EmployeeList() {
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [search, setSearch]       = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [departments, setDepts]   = useState([]);

  const fetchEmployees = useCallback(async () => {
    try {
      const [empRes, deptRes] = await Promise.all([
        api.get(`/users?search=${search}&department=${deptFilter}`),
        api.get('/departments'),   // structured departments (shows all, even 0-member)
      ]);
      setEmployees(empRes.data.data || []);
      // New endpoint returns [{id, name, ...}]; the users filter matches on name.
      setDepts((deptRes.data.departments || []).map(d => d.name));
    } catch { toast.error('Failed to load employees'); }
    finally { setLoading(false); }
  }, [search, deptFilter]);

  useEffect(() => { fetchEmployees(); }, [fetchEmployees]);

  const toggleStatus = async (id, current) => {
    try {
      await api.patch(`/users/${id}`, { is_active: !current });
      toast.success(`Employee ${!current ? 'activated' : 'deactivated'}`);
      fetchEmployees();
    } catch { toast.error('Failed to update status'); }
  };

  return (
    <div style={{ animation:'fadeIn 0.4s ease' }}>
      <div className="page-header" style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div>
          <h1 className="page-title">Employees</h1>
          <p className="page-sub">{employees.length} employees in your organisation</p>
        </div>
        <button className="btn btn-primary" onClick={() => navigate('/admin/employees/add')}>+ Add Employee</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20 }}>
        <input className="form-input" style={{ maxWidth:280 }} placeholder="Search by name..." value={search} onChange={e=>setSearch(e.target.value)} />
        <select className="form-select" style={{ width:'auto', padding:'12px 14px' }} value={deptFilter} onChange={e=>setDeptFilter(e.target.value)}>
          <option value="">All Departments</option>
          {departments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>
      </div>

      <div className="card">
        {loading ? <div style={{ display:'flex', justifyContent:'center', padding:40 }}><div className="spinner" style={{width:24,height:24}} /></div> :
        employees.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-icon">👥</div>
            <div className="empty-state-text">No employees found.<br/><span onClick={() => navigate('/admin/employees/add')} style={{ color:'#63dcbc', cursor:'pointer' }}>Add your first employee</span></div>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead><tr><th>Employee</th><th>Department</th><th>Designation</th><th>Shift</th><th>Type</th><th>Last Seen</th><th>Status</th><th></th></tr></thead>
              <tbody>
                {employees.map((emp,i) => (
                  <tr key={emp.id}>
                    <td>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div className="avatar avatar-sm" style={{ background:`linear-gradient(135deg,${COLORS[i%COLORS.length]},${COLORS[(i+1)%COLORS.length]})` }}>{getInitials(emp.full_name)}</div>
                        <div>
                          <div style={{ fontWeight:600, color:'#e2e8f0', fontSize:13 }}>{emp.full_name}</div>
                          <div style={{ fontSize:11, color:'#475569' }}>{emp.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ color:'#94a3b8' }}>{emp.department || '—'}</td>
                    <td style={{ fontSize:12 }}>{emp.designation || '—'}</td>
                    <td style={{ fontFamily:"'JetBrains Mono'", fontSize:11, color:'#64748b' }}>
                      {emp.shift_start?.slice(0,5)} – {emp.shift_end?.slice(0,5)}
                    </td>
                    <td><span className={`badge ${emp.is_remote?'badge-blue':'badge-green'}`}>{emp.is_remote?'Remote':'On-site'}</span></td>
                    <td style={{ fontSize:11, color:'#475569', fontFamily:"'JetBrains Mono'" }}>
                      {emp.last_seen ? new Date(emp.last_seen).toLocaleString('en-IN',{day:'numeric',month:'short',hour:'2-digit',minute:'2-digit'}) : 'Never'}
                    </td>
                    <td>
                      <span className={`badge ${emp.is_active?'badge-green':'badge-red'}`}>{emp.is_active?'Active':'Inactive'}</span>
                    </td>
                    <td>
                      <button className={`btn btn-sm ${emp.is_active?'btn-danger':'btn-secondary'}`} onClick={() => toggleStatus(emp.id, emp.is_active)}>
                        {emp.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
